import { writable, derived } from 'svelte/store';
const browser = typeof window !== 'undefined';
import { user } from './sessionStore';

// Using local API endpoints instead of direct API access
const API_BASE_URL = '/api/proxy';

// Default settings. Theme defaults to 'dark' to match legacy parity
// (legacy portal/src/stores/userSettingsStore.js:10 is the same). The
// bootstrap script in index.html also falls back to 'dark' when no
// localStorage value is present so first-paint matches the default.
const defaultSettings = {
  theme: 'dark',
  showBreadcrumbs: true,
  glucoseUnit: 'mg/dL',
  language: 'en_US',
  sidebarCollapsed: false
};

// Initial value: hydrate from localStorage so a page reload after an auth
// error doesn't bounce the user back to the default. The backend-loaded
// settings still win once the user store is populated (subscription below).
function initialSettings() {
  if (!browser) return defaultSettings;
  try {
    const stored = localStorage.getItem('portal_theme');
    if (stored === 'light' || stored === 'dark') {
      return { ...defaultSettings, theme: stored };
    }
  } catch (_) { /* localStorage unavailable */ }
  return defaultSettings;
}

// Create the user settings store
export const userSettings = writable(initialSettings());

// Track if settings are loaded from backend
export const settingsLoaded = writable(false);

// Track if there are unsaved changes
export const hasUnsavedChanges = writable(false);

// Function to extract settings from user object
function extractSettingsFromUser(userObj) {
  if (!userObj) return defaultSettings;
  
  return {
    theme: userObj.theme || defaultSettings.theme,
    showBreadcrumbs: userObj.showBreadcrumbs !== undefined ? userObj.showBreadcrumbs : defaultSettings.showBreadcrumbs,
    glucoseUnit: userObj.glucoseUnit || defaultSettings.glucoseUnit,
    language: userObj.language || defaultSettings.language,
    sidebarCollapsed: userObj.sidebarCollapsed !== undefined ? userObj.sidebarCollapsed : defaultSettings.sidebarCollapsed
  };
}

// Subscribe to user store changes and update settings automatically
if (browser) {
  user.subscribe(currentUser => {
    if (currentUser?.id) {
      // Check if the user object has settings fields
      const hasSettings = currentUser.theme !== undefined || 
                         currentUser.showBreadcrumbs !== undefined || 
                         currentUser.glucoseUnit !== undefined || 
                         currentUser.language !== undefined;
      
      if (hasSettings) {
        const settings = extractSettingsFromUser(currentUser);
        userSettings.set(settings);
        settingsLoaded.set(true);
      }
    }
  });
}

// Manual trigger function to force settings loading
export function triggerSettingsLoad() {
  if (!browser) return;
  
  let currentUser;
  user.subscribe(u => currentUser = u)();
  
  if (currentUser?.id) {
    // Check if the user object has settings fields
    const hasSettings = currentUser.theme !== undefined || 
                       currentUser.showBreadcrumbs !== undefined || 
                       currentUser.glucoseUnit !== undefined || 
                       currentUser.language !== undefined;
    
    if (hasSettings) {
      const settings = extractSettingsFromUser(currentUser);
      userSettings.set(settings);
      settingsLoaded.set(true);
    }
  }
}

// Load user settings from backend (legacy function, kept for compatibility)
export async function loadUserSettings() {
  if (!browser) {
    return;
  }

  // Get current user value
  let currentUser;
  user.subscribe(u => currentUser = u)();
  
  if (!currentUser?.id) {
    return;
  }
  
  // The settings will be loaded automatically via the user store subscription above
  // This function is now mainly for compatibility
}

// Save user settings to backend
export async function saveUserSettings(settings = null) {
  if (!browser) {
    return;
  }

  // Get current user value
  let currentUser;
  user.subscribe(u => currentUser = u)();
  
  if (!currentUser?.id) {
    return;
  }

  try {
    const settingsToSave = settings || get(userSettings);

    // Self-service endpoint. The old path (PUT /users/{id}) is admin-only, so
    // for site/study/support users every settings save 403'd silently and
    // Theme/Units reverted on reload. /users/me/settings accepts any
    // authenticated user and only the display-settings fields.
    const response = await fetch(`${API_BASE_URL}/users/me/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settingsToSave)
    });
    
    if (response.ok) {
      hasUnsavedChanges.set(false);
    } else {
      console.error('Failed to save user settings:', response.status);
    }
  } catch (error) {
    console.error('Error saving user settings:', error);
  }
}

// Update a specific setting
export function updateSetting(key, value) {
  userSettings.update(settings => {
    const newSettings = { ...settings, [key]: value };
    hasUnsavedChanges.set(true);
    return newSettings;
  });
}

// Update multiple settings at once
export function updateSettings(updates) {
  userSettings.update(settings => {
    const newSettings = { ...settings, ...updates };
    hasUnsavedChanges.set(true);
    return newSettings;
  });
}

// Auto-save settings when they change (debounced)
let saveTimeout;
userSettings.subscribe(settings => {
  if (browser) {
    let loaded, unsaved;
    settingsLoaded.subscribe(l => loaded = l)();
    hasUnsavedChanges.subscribe(u => unsaved = u)();
    
    if (loaded && unsaved) {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        saveUserSettings(settings);
      }, 1000); // Save after 1 second of no changes
    }
  }
});

// Helper function to get current value from store
function get(store) {
  let value;
  store.subscribe(v => value = v)();
  return value;
} 